import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { store } from "~/core/store";
import { BlurLevel, UseBackdropParams } from "./interfaces";

const BACKDROP = {
    element: null as HTMLDivElement | null,
    count: 0,
};

const BLURS = {
    sm: 2,
    md: 4,
    lg: 8,
} as const satisfies Record<BlurLevel, number>;

const mountBackdrop = (blur: BlurLevel) => {
    BACKDROP.count += 1;

    if (BACKDROP.element) {
        return;
    }

    const el = document.createElement("div");

    el.style.position = "fixed";
    el.style.top = "0";
    el.style.left = "0";
    el.style.width = "100%";
    el.style.height = "100%";
    el.style.zIndex = "100";
    el.style.backgroundColor = "rgba(164, 164, 164, 0.6)";
    el.style.backdropFilter = `blur(${BLURS[blur]}px)`;
    el.style.opacity = "0";
    el.setAttribute("data-backdrop", "");

    document.body.appendChild(el);
    BACKDROP.element = el;
    store.dialog.isBackdrop.value = true;

    el.getBoundingClientRect();

    el.style.opacity = "1";
    el.className = "transition duration-300";
};

const unmountBackdrop = async (timeout = 0) => {
    BACKDROP.count -= 1;

    if (timeout) {
        await new Promise((resolve) => window.setTimeout(resolve, timeout));
    }

    if (BACKDROP.count > 0 || !BACKDROP.element) {
        return;
    }

    const el = BACKDROP.element;

    el.style.opacity = "0";
    el.addEventListener("transitionend", () => {
        document.body.removeChild(el);
    });
    BACKDROP.element = null;
    store.dialog.isBackdrop.value = false;
};

const useBackdrop = <RefT extends HTMLElement = HTMLDivElement>({
    noTarget,
    skip,
    blur = "lg",
    unmountTimeout = 0,
}: UseBackdropParams = {}) => {
    const ref = useRef<RefT>(null);
    const target = useSignal<HTMLDivElement | null>(null);

    useEffect(() => {
        if (skip) {
            return;
        }

        const el = ref.current;

        if (!el && !noTarget) {
            return;
        }

        mountBackdrop(blur);

        let targetElement: HTMLDivElement;

        if (!noTarget && el) {
            targetElement = document.createElement("div");
            const rect = el.getBoundingClientRect();

            targetElement.style.position = "fixed";
            targetElement.style.top = `${rect.top}px`;
            targetElement.style.left = `${rect.left}px`;
            targetElement.style.width = `${rect.width}px`;
            targetElement.style.height = `${rect.height}px`;
            targetElement.style.zIndex = "101";
            targetElement.style.opacity = "0";

            document.body.appendChild(targetElement);
            target.value = targetElement;

            targetElement.getBoundingClientRect();

            targetElement.style.opacity = "1";
            targetElement.className = "transition duration-100 ease-out";
        }

        return () => {
            unmountBackdrop(unmountTimeout);

            if (targetElement) {
                document.body.removeChild(targetElement);
            }
        };
    }, [target, noTarget, skip, blur, unmountTimeout]);

    return { ref, target };
};

export default useBackdrop;
